import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XCircle, Eye, EyeOff, Loader2, X, AlertTriangle, RotateCcw, Pencil } from 'lucide-react';
import { useCloseAdMutation, useReopenAdMutation, useToggleBidsVisibilityMutation } from '@nimbazaar/store';
import { useToast } from './Toast';

interface AdOwnerControlsProps {
  adId: string;
  adSlug: string;
  status: string;
  bidsVisible: boolean;
}

export function AdOwnerControls({ adId, adSlug, status, bidsVisible }: AdOwnerControlsProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const [closeAd, { isLoading: isClosing }] = useCloseAdMutation();
  const [reopenAd, { isLoading: isReopening }] = useReopenAdMutation();
  const [toggleVisibility, { isLoading: isToggling }] = useToggleBidsVisibilityMutation();

  if (status !== 'ACTIVE' && status !== 'CLOSED') return null;

  const handleClose = async () => {
    try {
      await closeAd(adId).unwrap();
      toast.success('Ad Closed', 'Your ad has been closed successfully.');
      setShowCloseConfirm(false);
    } catch {
      toast.error('Error', 'Failed to close the ad. Please try again.');
    }
  };

  const handleReopen = async () => {
    try {
      await reopenAd(adId).unwrap();
      toast.success('Ad Reopened', 'Your ad is now active again with a new 7-day expiry.');
    } catch {
      toast.error('Error', 'Failed to reopen the ad. Please try again.');
    }
  };

  const handleToggleBids = async () => {
    try {
      await toggleVisibility(adId).unwrap();
      toast.success(
        'Updated',
        bidsVisible ? 'Bids are now hidden from the public.' : 'Bids are now visible to everyone.'
      );
    } catch {
      toast.error('Error', 'Failed to update bids visibility.');
    }
  };

  return (
    <>
      <div className="border-t border-gray-100 mt-6 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Ad Controls</h3>
        <div className="flex flex-wrap items-center gap-3">
          {status === 'ACTIVE' && (
            <>
              {/* Edit Ad */}
              <button
                onClick={() => navigate(`/edit-ad/${adId}`)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Edit Ad
              </button>

              {/* Toggle Bids Visibility */}
              <button
                onClick={handleToggleBids}
                disabled={isToggling}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isToggling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : bidsVisible ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                {bidsVisible ? 'Hide Bids' : 'Show Bids'}
              </button>

              {/* Close Ad */}
              <button
                onClick={() => setShowCloseConfirm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Close Ad
              </button>
            </>
          )}

          {status === 'CLOSED' && (
            <button
              onClick={handleReopen}
              disabled={isReopening}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors disabled:opacity-50"
            >
              {isReopening ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              Reopen Ad
            </button>
          )}
        </div>
      </div>

      {/* Close Confirmation Modal */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCloseConfirm(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Close Ad</h2>
              </div>
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-gray-600 text-sm">
                Are you sure you want to close this ad?
                The ad will no longer be visible to other users and no new bids can be placed.
                You can reopen it later from the ad detail page.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClose}
                disabled={isClosing}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isClosing && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, Close Ad
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
